import { createStoreItem } from "@editor/items/ItemStore";
import type { ConditionItem } from "@editor/items/condition/ConditionItem";
import type { Migration } from "./Migration.js";
import type { ItemStoreState } from "@editor/items/ItemStore";
import type { StoreItem } from "@editor/items/StoreItem";
import type { IncidentTriggerItem } from "@editor/items/incidentTrigger/IncidentTriggerItem";
import type { ActionItem } from "@editor/items/action/ActionItem";
import type { IncidentItem } from "@editor/items/incident/IncidentItem";

import migrateCondition from "@editor/items/condition/migrations/2.8.0_change_structure.js";

/**
 * Legacy ConditionItem
 * @deprecated
 */
interface LegacyConditionItem extends StoreItem {
  id: number;
  type: "condition";

  activatorType:
    | "element"
    | "resource"
    | "index"
    | "measure"
    | "incident"
    | "round"
    | "roundRange"
    | "always"
    | "measureActivation"
    | "incidentActivation";
  activatorId: number | null;
  operator: "larger" | "smaller" | "equals";
  entryType: "constant" | "singular"; // constant: always, singular: on status change
  value: number;
  valueB: number;
  doc: string;

  srcType: "scenarioEnd" | "measure" | "incidentTrigger";
  srcId: number | null;

  order: number;
}

type FollowingIncident = {
  id: number;
  chance: number;
  roundDelay: number;
};

const migration: Migration = {
  applyToVersions: "<2.8.0",
  type: "before",
  name: "update conditions to new format",
  migrate: (data: {
    measures?: ItemStoreState<
      ActionItem & {
        followingIncidents?: { [id: string]: FollowingIncident };
      }
    >;
    incidents?: ItemStoreState<
      IncidentItem & {
        followingIncidents?: { [id: string]: FollowingIncident };
      }
    >;
    conditions?:
      | ItemStoreState<ConditionItem>
      | ItemStoreState<LegacyConditionItem>;
    incidentTriggers?: ItemStoreState<IncidentTriggerItem>;
  }) => {
    if (!data.conditions) return data;
    if (!data.incidentTriggers) return data;

    const allConditions = Object.values(data.conditions.items);
    if (allConditions.length === 0) return data;

    // Check if data is already migrated:
    if ("comparedType" in allConditions[0]) return data;

    // To prepare, make sure all triggers that do not have any conditions
    // have a chance of 0%.
    // This is necessary because we're removing the "always" condition,
    // instead all triggers without conditions will be active by default.
    for (const triggerId in data.incidentTriggers.items) {
      const trigger = data.incidentTriggers.items[triggerId];

      const conditions = allConditions.filter(
        (condition) =>
          condition.srcId === trigger.id &&
          condition.srcType === "incidentTrigger"
      );

      if (conditions.length === 0) {
        console.log("set chance to 0 for trigger without conditions", trigger);
        trigger.chance = 0;
      }
    }

    // Migrate conditions:
    for (const conditionId in data.conditions.items) {
      const oldCondition = data.conditions.items[
        conditionId
      ] as LegacyConditionItem;

      const updatedCondition = migrateCondition(
        data.conditions as ItemStoreState<ConditionItem>,
        oldCondition
      );

      data.conditions.items[conditionId] = updatedCondition;
    }

    if (data.incidents && data.conditions) {
      // Migrate following incidents to conditions:
      for (const incidentId in data.incidents.items) {
        const incident = data.incidents.items[incidentId];

        for (const followingIncidentId in incident.followingIncidents) {
          const followingIncident =
            incident.followingIncidents[followingIncidentId];

          createFollowingIncidentTrigger(followingIncident, incident, {
            incidentTriggers: data.incidentTriggers,
            conditions: data.conditions as ItemStoreState<ConditionItem>
          });
        }
      }
    }

    if (data.measures && data.conditions) {
      // Migrate following incidents to conditions:
      for (const measureId in data.measures.items) {
        const measure = data.measures.items[measureId];

        for (const followingIncidentId in measure.followingIncidents) {
          const followingIncident =
            measure.followingIncidents[followingIncidentId];

          createFollowingIncidentTrigger(followingIncident, measure, {
            incidentTriggers: data.incidentTriggers,
            conditions: data.conditions as ItemStoreState<ConditionItem>
          });
        }
      }
    }

    return data;
  }
};

/**
 * Create a new trigger with a condition that works like a following incident.
 */
function createFollowingIncidentTrigger(
  followingIncident: FollowingIncident,
  source: ActionItem | IncidentItem,
  data: {
    incidentTriggers: ItemStoreState<IncidentTriggerItem>;
    conditions: ItemStoreState<ConditionItem>;
  }
) {
  // create an incident trigger for the following incident:
  const newTrigger = createStoreItem(data.incidentTriggers, {
    chance: followingIncident.chance,
    incidentId: followingIncident.id,
    maxOccurrences: null
  });

  // create a condition for the trigger
  const newCondition = createCondition(data.conditions, {
    srcType: "incidentTrigger",
    srcId: newTrigger.id,

    activatorType: source.type,
    activatorId: source.id,
    activatorProperty: "activationCount",

    operator: "?",

    activatorStartType: "relativeRound",
    activatorStart: followingIncident.roundDelay,
    activatorEndType: null,
    activatorEnd: null,

    comparedType: "value",
    comparedValue: 0,
    comparedValueType: "number"
  });
  console.log("create new incident trigger condition", newCondition);
}

function createCondition(
  conditionStoreState: ItemStoreState<ConditionItem>,
  conditionData: Partial<ConditionItem> &
    Pick<ConditionItem, "srcType" | "srcId">
) {
  return createStoreItem(conditionStoreState, conditionData);
}

export default migration;
